// Os recados escritos pelo vigia da Vick — o "Resumo do dia" e o "Alerta de
// gestão".
//
// Eles não estão em `ia_alertas`. O vigia detecta os alertas, manda a lista
// para a IA redigir UM texto por módulo e grava esse texto em `notifications`
// (`type = 'ia_alerta'`). O alerta é o fato; o recado é a leitura do fato.
//
// Só que a caixa de notificação corta o texto em duas linhas e a tela /vick
// nunca mostrou o recado — clicar em "Resumo do dia" abria a fila de alertas e
// o texto que a pessoa queria ler não estava em lugar nenhum do sistema. Este
// hook existe para fechar esse buraco.
//
// Leitura direta pela Data API: `notifications` já é filtrada por `user_id`, e
// recado é pessoal — quem não estava na lista de destinatários não recebeu.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { IaAlertaModulo } from '../../types';

export interface VickRecado {
  id: string;
  /** "Resumo do dia" (média/baixa, uma vez ao dia) ou "Alerta de gestão" (alta, na hora). */
  titulo: string;
  texto: string;
  criadoEm: string;
  lido: boolean;
  modulo?: IaAlertaModulo;
  /** Ids dos alertas que este recado reuniu. */
  alertas: number[];
}

/** Quantos recados guardar à vista. Mais que isso vira arquivo, não painel. */
const LIMITE = 15;

export function useVickRecados() {
  const { user } = useAuth();
  const userId = user?.id;
  const [recados, setRecados] = useState<VickRecado[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!userId) {
      setRecados([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, content, created_at, read, metadata')
        .eq('user_id', userId)
        .eq('type', 'ia_alerta')
        .order('created_at', { ascending: false })
        .limit(LIMITE);
      if (error) throw error;

      setRecados(
        (data ?? []).map((n: any) => ({
          id: String(n.id),
          titulo: n.title ?? 'Recado da Vick',
          texto: n.content ?? '',
          criadoEm: n.created_at,
          lido: !!n.read,
          modulo: n.metadata?.modulo as IaAlertaModulo | undefined,
          alertas: Array.isArray(n.metadata?.alertas) ? n.metadata.alertas : [],
        })),
      );
    } catch (e: any) {
      console.error('Erro ao carregar os recados da Vick:', e);
      setErro(e?.message || 'Não foi possível carregar os resumos.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return { recados, loading, erro, recarregar: carregar };
}
